/** @jsxImportSource @emotion/react */
import React, { useContext, useMemo, useState, useRef, useEffect } from 'react';
import BigNumber from 'bignumber.js';
import { Paper, Typography } from '@mui/material';
import { Link } from 'react-router-dom';
import { TOKENS } from 'constants/tokens';
import { ethers } from 'ethers';

import {
    useGetCurrentVotes,
    useGetVoteDelegateAddress,
    useSetInviter, GetVoteDelegateAddressOutput, useGetInviter, GetInviterOutput, useGetVaultLevel, useGetUpline, GetVaultLevelOutput, GetUplineOutput,
    useGetAllUsers, useGetDirectReferralCount, useGetReferrerInfo,
    useGetAllUsersInNetwork, useGetNetworkRewardDetails,
} from 'clients/api';
import { AuthContext } from 'context/AuthContext';
import { Delimiter, EllipseText, Icon, LinkButton, PrimaryButton, Tooltip } from 'components';
import PATHS from 'constants/path';
import useSuccessfulTransactionModal from 'hooks/useSuccessfulTransactionModal';
import { useTranslation } from 'translation';
import { convertWeiToTokens, restService, getContractAddress } from 'utilities';
import DelegateModal from '../DelegateModal';
import { useStyles } from './styles';
import VotingWalletUiNew from './VotingWalletUi';


interface IVotingWalletUiProps {
    connectedWallet: boolean;
    openAuthModal: () => void;
    currentUserAccountAddress: string | undefined;

    delegateModelIsOpen: boolean;
    setDelegateModelIsOpen: (open: boolean) => void;
    setDelegateModelAddress: (address: string) => void;
    delegate: GetInviterOutput | undefined;
    setVoteDelegationMutation: (params: { inviterAddress: string; accountAddress: string }) => Promise<any>;
    isVoteDelegationLoading: boolean;
    
    // New properties needed by VotingWalletUiNew
    currentUserDelegateAddress?: string;
    isLoading: boolean;
    delegateModelAddress: string;
    vaultAddress: string;
    allUsers: any;
    networkStats: any;
    directReferralCount: any;
    referrerInfo: any;
    allUsersInNetwork: any;
    networkRewardDetails: any;
}

const PAGE_SIZE = 5;

export const VotingWalletUi: React.FC<IVotingWalletUiProps> = ({
  connectedWallet,
  openAuthModal,
  currentUserAccountAddress,
  delegateModelIsOpen,
  setDelegateModelIsOpen,
  setDelegateModelAddress,
  delegate,
  setVoteDelegationMutation,
  isVoteDelegationLoading,
  currentUserDelegateAddress,
  isLoading,
  delegateModelAddress,
  vaultAddress,
  allUsers,
  networkStats,
  directReferralCount,
  referrerInfo,
  allUsersInNetwork,
  networkRewardDetails,
}) => {
  const { t, Trans } = useTranslation();
  const styles = useStyles();


  const previouslyDelegated = !!delegate;
  // const userHasLockedXVS = userStakedWei.isGreaterThan(0);
    return (
    <div css={styles.root}>
      <Typography variant="h4">网体管理中心</Typography>
      
      {/* 推荐人信息卡片 */}
      <Paper css={styles.votingWalletPaper} style={{ marginBottom: '16px' }}>
        <Typography variant="h5" style={{ marginBottom: '16px', color: '#1976d2' }}>
          推荐关系
        </Typography>
        
        {(delegate?.inviter !== '0x0000000000000000000000000000000000000000' || referrerInfo?.hasReferrer) && (
          <div css={styles.votingWeightContainer}>
            <Typography variant="body2" css={styles.subtitle}>
              您的推荐人
            </Typography>
            <Typography className="ellipse-text" css={styles.address}>
              {referrerInfo?.referrer || delegate?.inviter}
            </Typography>
          </div>
        )}
        
        <div css={styles.votingWeightContainer}>
          <Typography variant="body2" css={styles.subtitle}>
            Tax Level
          </Typography>
          <Typography className="ellipse-text" css={styles.address}>
            {networkStats?.level?.toFixed()}
          </Typography>
        </div>
      </Paper>

      {/* 网体统计卡片 */}
      <Paper css={styles.votingWalletPaper} style={{ marginBottom: '16px' }}>
        <Typography variant="h5" style={{ marginBottom: '16px', color: '#1976d2' }}>
          团队数据
        </Typography>

        {/* 统计数据网格 */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(2, 1fr)',
          gap: '16px',
          marginBottom: '16px'
        }}>
          <div style={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            borderRadius: '12px',
            padding: '20px',
            textAlign: 'center',
            color: 'white'
          }}>
            <Typography variant="h3" style={{ color: 'white', marginBottom: '4px' }}>
              {directReferralCount?.count || referrerInfo?.directReferrals || networkStats?.upline?.length || 0}
            </Typography>
            <Typography variant="small2" style={{ color: 'rgba(255,255,255,0.8)' }}>
              直推人数
            </Typography>
          </div>

          <div style={{
            background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
            borderRadius: '12px',
            padding: '20px',
            textAlign: 'center',
            color: 'white'
          }}>
            <Typography variant="h3" style={{ color: 'white', marginBottom: '4px' }}>
              {allUsers?.count || 0}
            </Typography>
            <Typography variant="small2" style={{ color: 'rgba(255,255,255,0.8)' }}>
              团队总人数
            </Typography>
          </div>
        </div>

        <div style={{
          background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
          borderRadius: '12px',
          padding: '20px',
          textAlign: 'center',
          color: 'white'
        }}>
          <Typography variant="h3" style={{ color: 'white', marginBottom: '4px' }}>
            {0}
          </Typography>
          <Typography variant="small2" style={{ color: 'rgba(255,255,255,0.8)' }}>
            活跃质押用户
          </Typography>
        </div>

        {/* 团队概览信息 */}
        {allUsers && allUsers.count > 0 && (
          <>
            <Delimiter css={styles.delimiter} />
            <div style={{
              background: '#f8f9fa',
              borderRadius: '8px',
              padding: '16px',
            }}>
              <Typography variant="body2" style={{ color: '#666', marginBottom: '8px' }}>
                团队概览
              </Typography>
              <Typography variant="small2" style={{ color: '#888' }}>
                • 您的推荐网络包含 {allUsers?.count || 0} 位成员
              </Typography>
              <Typography variant="small2" style={{ color: '#888' }}>
                • 其中 {0} 位正在参与质押
              </Typography>
              <Typography variant="small2" style={{ color: '#888' }}>
                • 直接推荐了 {directReferralCount?.count || 0} 位用户
              </Typography>
            </div>
          </>
        )}
      </Paper>

      {/* 操作按钮卡片 */}
      <Paper css={styles.votingWalletPaper}>
        <Typography variant="h5" style={{ marginBottom: '16px', color: '#1976d2' }}>
          操作面板
        </Typography>
        
        {!connectedWallet && (
          <PrimaryButton css={styles.actionButton} onClick={openAuthModal}>
            {t('connectWallet.connectButton')}
          </PrimaryButton>
        )}
        
        {connectedWallet && (
          <PrimaryButton 
            css={styles.actionButton} 
            onClick={() => setDelegateModelIsOpen(true)} 
            disabled={previouslyDelegated && delegate?.inviter !== '0x0000000000000000000000000000000000000000'}
          >
            {previouslyDelegated && delegate?.inviter !== '0x0000000000000000000000000000000000000000' ? '重新绑定' : '绑定推荐人'}
          </PrimaryButton>
        )}
      </Paper>
    </div>
  );
};

const VotingWallet: React.FC = () => {
    const { account } = useContext(AuthContext);
    // TODO: handle loading state (see https://app.clickup.com/t/2d4rcee)
    // const {
    //   data: { assets },
    // } = useGetUserMarketInfo({
    //   accountAddress: account?.address,
    // });
    const { account: { address: accountAddress } = { address: undefined }, openAuthModal } =
        useContext(AuthContext);

    const { data: delegate } = useGetInviter(
        { accountAddress: accountAddress || '' },
        { enabled: !!accountAddress },
    );

    const { data: Level } = useGetVaultLevel(
        { accountAddress: accountAddress || '' },
        { enabled: !!accountAddress },
    );

    const { data: Upline } = useGetUpline(
        { accountAddress: accountAddress || '' },
        { enabled: !!accountAddress },
    );

    // 新增的网体查询
    const { data: allUsers } = useGetAllUsers(
        { accountAddress: accountAddress || '' },
        { enabled: !!accountAddress },
    );



    const { data: directReferralCount } = useGetDirectReferralCount(
        { accountAddress: accountAddress || '' },
        { enabled: !!accountAddress },
    );

    const { data: referrerInfo } = useGetReferrerInfo(
        { accountAddress: accountAddress || '' },
        { enabled: !!accountAddress },
    );

    const { data: allUsersInNetwork } = useGetAllUsersInNetwork(
        { accountAddress: accountAddress || '', maxLevels: 17 },
        { enabled: !!accountAddress },
    );

    const { data: networkRewardDetails } = useGetNetworkRewardDetails(
        { accountAddress: accountAddress || '' },
        { enabled: !!accountAddress },
    );

  const [delegateModelIsOpen, setDelegateModelIsOpen] = useState(false);
  const [delegateModelAddress, setDelegateModelAddress] = useState('');
  const { t } = useTranslation();
    const userStakedWei = new BigNumber(0);
    const XVS_TOKEN_ID = 'umi';
    const { openSuccessfulTransactionModal } = useSuccessfulTransactionModal();
    const { mutateAsync: setVoteDelegationMutation, isLoading: isVoteDelegationLoading } = useSetInviter(
        {
            onSuccess: data => {
                setDelegateModelIsOpen(false);
                // openSuccessfulTransactionModal({
                //     title: t('vote.successfulDelegationModal.title'),
                //     content: t('vote.successfulDelegationModal.message'),
                //     amount: {
                //         tokenId: TOKENS.umi,
                //         valueWei: userStakedWei,
                //     },
                //     transactionHash: data.transactionHash,
                // });
            },
        },
    );

  return (
      <>
          <VotingWalletUiNew
            connectedWallet={!!accountAddress}
            openAuthModal={openAuthModal}
            currentUserAccountAddress={accountAddress}
            delegateModelIsOpen={delegateModelIsOpen}
            setDelegateModelIsOpen={setDelegateModelIsOpen}
            currentUserDelegateAddress={delegate?.inviter}
            isLoading={isVoteDelegationLoading}
            delegateModelAddress={delegateModelAddress}
            setDelegateModelAddress={setDelegateModelAddress}
            delegate={delegate}
            setVoteDelegationMutation={setVoteDelegationMutation}
            isVoteDelegationLoading={isVoteDelegationLoading}
            vaultAddress=""
            allUsers={allUsers}
            networkStats={undefined}
            directReferralCount={directReferralCount}
            referrerInfo={referrerInfo}
            allUsersInNetwork={allUsersInNetwork}
            networkRewardDetails={networkRewardDetails}
          />
          {/* <Table inviter={getInviterData.inviters} /> */}
      </>

  );
};

export default VotingWallet;
